import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import SearchBar from "formElements/inputs/SearchBar.component";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    padding: 10,
    gap: 10,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 10,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
  },
  labelText: {
    fontSize: 18,
    color: theme.color.backgroundText,
  },
  labelTextBold: {
    fontWeight: theme.font.weight.semibold,
  },
  userListContainer: {
    marginLeft: 16,
    marginTop: 4,
    marginBottom: 8,
  },
  userRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  userText: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
  },
});

export default function LocationSelectionModal(props) {
  const {
    open,
    onClose,
    selectedLocationIds,
    setSelectedLocationIds,
    recipients,
    setRecipients,
  } = props;

  const { organizationColor, personList, locationList } =
    useContext(OrganizationContext);

  const [searchText, setSearchText] = useState("");
  const [localLocationIds, setLocalLocationIds] = useState(new Set());
  const [localUserIds, setLocalUserIds] = useState(new Set());

  // Create a mapping of locationId to persons in that location
  const personsByLocation = useMemo(() => {
    const map = {};
    if (!personList || !Array.isArray(personList)) {
      return map;
    }

    personList.forEach((person) => {
      // Check for both old direct locationId and new _associations.Locations structure
      const locationIds = [];

      // Old structure (direct locationId)
      if (person.locationId) {
        locationIds.push(person.locationId);
      }

      // New structure (_associations.Locations array)
      if (
        person._associations?.Locations &&
        person._associations.Locations.length > 0
      ) {
        person._associations.Locations.forEach((location) => {
          if (location.locationId) {
            locationIds.push(location.locationId);
          }
        });
      }

      // Add person to all their associated locations
      locationIds.forEach((locationId) => {
        const locationKey = String(locationId);
        if (!map[locationKey]) {
          map[locationKey] = [];
        }
        map[locationKey].push(person);
      });
    });
    return map;
  }, [personList]);

  useEffect(() => {
    // Initialize with current props
    const currentLocationIds =
      selectedLocationIds && Array.isArray(selectedLocationIds)
        ? new Set(selectedLocationIds.map((id) => String(id)))
        : new Set();
    const currentRecipients =
      recipients && Array.isArray(recipients) ? new Set(recipients) : new Set();

    // Calculate which locations should be marked as selected based on recipients
    // This handles update mode where recipients exist but locations need to be calculated
    if (
      recipients &&
      Array.isArray(recipients) &&
      recipients.length > 0 &&
      Object.keys(personsByLocation).length > 0
    ) {
      const fullySelectedLocationIds = new Set();

      // Check each location to see if all its members are in recipients
      Object.entries(personsByLocation).forEach(([locationId, persons]) => {
        if (persons && persons.length > 0) {
          const allMembersSelected = persons.every((person) =>
            currentRecipients.has(person.personId),
          );
          if (allMembersSelected) {
            fullySelectedLocationIds.add(locationId);
          }
        }
      });

      // Merge with explicitly selected locations
      const finalLocationIds = new Set([
        ...currentLocationIds,
        ...fullySelectedLocationIds,
      ]);

      // Set the final state - ensure locations and users are consistent
      setLocalLocationIds(finalLocationIds);
      setLocalUserIds(currentRecipients);
    } else {
      // No recipients or personsByLocation not ready, just use the props as-is
      setLocalLocationIds(currentLocationIds);
      setLocalUserIds(currentRecipients);
    }
  }, [selectedLocationIds, recipients, personsByLocation]);

  const toggleLocation = useCallback(
    (locationId) => {
      const locationKey = String(locationId);
      const updatedLocations = new Set(localLocationIds);
      const updatedUsers = new Set(localUserIds);
      const locationUserIds = (personsByLocation[locationKey] || [])
        .map((person) => person?.personId)
        .filter(Boolean);

      if (updatedLocations.has(locationKey)) {
        updatedLocations.delete(locationKey);
        locationUserIds.forEach((userId) => {
          if (userId) {
            updatedUsers.delete(userId);
          }
        });
      } else {
        updatedLocations.add(locationKey);
        locationUserIds.forEach((userId) => {
          if (userId) {
            updatedUsers.add(userId);
          }
        });
      }

      setLocalLocationIds(updatedLocations);
      setLocalUserIds(updatedUsers);
    },
    [localLocationIds, localUserIds, personsByLocation],
  );

  const toggleUser = useCallback(
    (locationId, userId) => {
      const locationKey = String(locationId);
      const updatedLocations = new Set(localLocationIds);
      const updatedUsers = new Set(localUserIds);

      if (updatedUsers.has(userId)) {
        updatedUsers.delete(userId);
      } else {
        updatedUsers.add(userId);
      }

      const locationUserIds = (personsByLocation[locationKey] || [])
        .map((person) => person?.personId)
        .filter(Boolean);
      const isLocationFullySelected =
        locationUserIds.length > 0 &&
        locationUserIds.every((locationUserId) => {
          return updatedUsers.has(locationUserId);
        });

      if (isLocationFullySelected) {
        updatedLocations.add(locationKey);
      } else {
        updatedLocations.delete(locationKey);
      }

      setLocalLocationIds(updatedLocations);
      setLocalUserIds(updatedUsers);
    },
    [localLocationIds, localUserIds, personsByLocation],
  );

  const eligibleLocations = useMemo(() => {
    if (!locationList || !Array.isArray(locationList)) {
      return [];
    }
    return locationList;
  }, [locationList]);

  const areAllLocationsSelected = useMemo(() => {
    if (eligibleLocations.length === 0) {
      return false;
    }

    const allSelected = eligibleLocations.every((location) => {
      return localLocationIds.has(String(location.locationId));
    });

    return allSelected;
  }, [eligibleLocations, localLocationIds]);

  const toggleSelectAll = useCallback(() => {
    if (areAllLocationsSelected) {
      setLocalLocationIds(new Set());
      setLocalUserIds(new Set());
      return;
    }

    const newLocationIds = new Set(
      eligibleLocations.map((location) => String(location.locationId)),
    );
    const newUserIds = new Set(
      eligibleLocations.flatMap((location) => {
        const locationKey = String(location.locationId);
        return (personsByLocation[locationKey] || [])
          .map((person) => person?.personId)
          .filter(Boolean);
      }),
    );

    setLocalLocationIds(newLocationIds);
    setLocalUserIds(newUserIds);
  }, [areAllLocationsSelected, eligibleLocations, personsByLocation]);

  const onSave = useCallback(() => {
    // Convert back to original type when saving
    const locationIdsArray = [...localLocationIds];
    setSelectedLocationIds(locationIdsArray);
    setRecipients([...localUserIds]);
    onClose(locationIdsArray);
  }, [
    localLocationIds,
    localUserIds,
    onClose,
    setSelectedLocationIds,
    setRecipients,
  ]);

  const filterLocation = useCallback(
    (location) => {
      const normalizedSearch = searchText.toLowerCase();

      if (location.name.toLowerCase().includes(normalizedSearch)) {
        return true;
      }

      // Check if any members in this location match
      const locationKey = String(location.locationId);
      const members = personsByLocation[locationKey] || [];
      const hasMatchingMember = members.some((user) => {
        if (!user || !user.firstName || !user.lastName) {
          return false;
        }
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(normalizedSearch);
      });

      return hasMatchingMember;
    },
    [searchText, personsByLocation],
  );

  return (
    <CustomModal open={open} onClose={onSave} title="Select Locations" scroll>
      <View style={classes.root}>
        <SearchBar value={searchText} onChangeText={setSearchText} />

        <PressableContainer style={classes.row} onPress={toggleSelectAll}>
          <Text style={[classes.labelText, classes.labelTextBold]}>
            All Locations
          </Text>
          <Icon
            name={areAllLocationsSelected ? "check" : "checkbox-blank-outline"}
            size={28}
            color={organizationColor}
          />
        </PressableContainer>

        <View>
          {(() => {
            const filteredLocations = eligibleLocations.filter((location) =>
              filterLocation(location),
            );
            return filteredLocations.map((location) => {
              const locationKey = String(location.locationId);
              const users = (personsByLocation[locationKey] || []).sort(
                (a, b) => a.lastName.localeCompare(b.lastName),
              );
              const isLocationSelected = localLocationIds.has(locationKey);
              const isDisabled = users.length === 0;
              return (
                <View key={location.locationId}>
                  <PressableContainer
                    style={classes.row}
                    onPress={() => {
                      toggleLocation(location.locationId);
                    }}
                    disabled={isDisabled}
                  >
                    <Text style={classes.labelText}>{location.name}</Text>

                    {!isDisabled && (
                      <Icon
                        name={
                          isLocationSelected
                            ? "check"
                            : "checkbox-blank-outline"
                        }
                        size={28}
                        color={organizationColor}
                      />
                    )}
                  </PressableContainer>

                  {/* Show users in this location */}
                  {users.length > 0 ? (
                    <View style={classes.userListContainer}>
                      {users.map((user) => {
                        const isUserSelected = localUserIds.has(user.personId);

                        return (
                          <PressableContainer
                            key={user.personId}
                            style={classes.userRow}
                            onPress={() => {
                              toggleUser(location.locationId, user.personId);
                            }}
                          >
                            <Text style={classes.userText}>
                              {user.firstName} {user.lastName}
                            </Text>
                            <Icon
                              name={
                                isUserSelected
                                  ? "check"
                                  : "checkbox-blank-outline"
                              }
                              size={22}
                              color={organizationColor}
                            />
                          </PressableContainer>
                        );
                      })}
                    </View>
                  ) : (
                    <Text style={[classes.userListContainer, classes.userText]}>
                      No associated individuals
                    </Text>
                  )}
                </View>
              );
            });
          })()}
        </View>
      </View>
    </CustomModal>
  );
}
